import { cn } from '@/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { LoadingDots } from './loading-dots'

type ButtonProps = ButtonVariantProps &
  ButtonHTMLAttributes<HTMLButtonElement> &
  Readonly<{
    asChild?: boolean
    unstyled?: boolean
    loading?: boolean
  }>

type ButtonVariantProps = VariantProps<typeof buttonVariants>

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      loading,
      children,
      size,
      className,
      disabled,
      shape,
      type = 'button',
      asChild,
      unstyled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        tabIndex={0}
        type={type}
        disabled={!!disabled}
        className={cn(
          !unstyled && buttonVariants({ variant, size, shape }),
          loading && 'disabled:bg-neutral-600 disabled:opacity-100',
          className,
        )}
        role='button'
        {...props}
      >
        {loading ? (
          <>
            <span className='invisible flex gap-2'>{children}</span>
            <LoadingDots className='absolute' />
          </>
        ) : (
          children
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

const WHITE_SPINNER_VARIANTS: ButtonVariantProps['variant'][] = [
  'black',
  'blueberry',
  undefined,
  null,
]

const buttonVariants = cva(
  'select-none flex transition-[transform,box-shadow] ease-in-out active:scale-[.975] disabled:opacity-60 focus-visible:outline-0 focus-visible:ring-[3px] focus-visible:ring-neutral-75 disabled:pointer-events-none items-center justify-center w-fit relative whitespace-nowrap gap-2 font-normal uppercase',
  {
    variants: {
      variant: {
        'black-outline': 'bg-neutral-25 border-neutral-600 border text-neutral-500',
        'red-outline': 'bg-neutral-25 border-error-300 border text-error-300',
        black: 'bg-neutral-600 text-neutral-white disabled:bg-grey-300',
        grey: 'border-neutral-75 border text-sm rounded-sm text-neutral-500',
        blueberry:
          'bg-mango-300 text-neutral-white disabled:bg-neutral-75 disabled:text-neutral-150',
        mango: 'bg-mango-300 text-neutral-white disabled:bg-neutral-75 disabled:text-neutral-150',
        'mango-outline': 'bg-mango-50 border-mango-200 border text-neutral-600',
        neutal:
          'bg-neutral-600 text-neutral-white disabled:bg-neutral-75 disabled:text-neutral-150',
      },
      shape: { round: 'rounded-full', square: 'rounded' },
      size: {
        xs: 'text-xs h-7 px-3',
        sm: 'text-xs h-9 px-4',
        md: 'text-sm h-10 px-4',
        lg: 'text-base h-12 px-5',
      },
    },
    defaultVariants: {
      variant: 'neutal',
      shape: 'round',
      size: 'md',
    },
  },
)

export { Button, buttonVariants, type ButtonProps }
